import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress, Tooltip } from '@mui/material';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  Header,
  HeaderGroup,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  useReactTable
} from '@tanstack/react-table';
import { MAX_TABLE_ROWS } from 'constants/constants';

import EmptyState from 'components/EmptyState/EmptyState';

import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../ui';

import DataTablePagination from './components/DataTablePagination';
import { geTableSelectionColumn, SELECT_ALL_ROWS_ID } from './utils';

export type Size = 'sm' | 'md' | 'lg';

export interface DataTableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T>[];
  rowIdIdentifier?: string;
  showHeader?: boolean;
  showDataAggregatedRow?: boolean;
  aggregatedRowData?: { title: string; amount: number };
  size?: Size;
  className?: string;
  showPagination?: boolean;
  rowsPerPage?: number;
  bodyDataTestId?: string;
  isLoading?: boolean;
  emptyStateConfig?: {
    title: string;
    isEmpty: boolean;
    description: React.ReactNode;
    onClick: () => void;
  };
  bordered?: boolean;
  manualPagination?: boolean;
  pageIndex?: number;
  pageCount?: number;
  onPageChange?: (newPageIndex: number) => void;
  rowCount?: number;
  getRowId?: (row: T) => string;
  rowSelectionConfig?: RowSelectionConfig<T>;
}

type RowSelectionConfig<T> = {
  enableRowSelection?: boolean;
  isCheckboxesDisabled?: boolean;
  selectedRowsState?: RowSelectionState;
  onRowSelectionChange?: (selectedRows: T[]) => void;
};

const sizeClasses = {
  sm: 'h-12',
  md: 'h-16',
  lg: 'h-24'
};

const DataTable = <T extends object>({
  data = [],
  columns,
  className,
  rowIdIdentifier = '',
  showHeader = true,
  size = 'sm',
  showPagination = true,
  rowsPerPage = MAX_TABLE_ROWS,
  bodyDataTestId = '',
  isLoading = false,
  emptyStateConfig,
  bordered = false,
  manualPagination = false,
  pageIndex: controlledPageIndex = 0,
  pageCount: controlledPageCount = 0,
  onPageChange,
  rowCount,
  rowSelectionConfig,
  getRowId
}: DataTableProps<T>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionConfig?.selectedRowsState || {}
  );
  const { enableRowSelection, onRowSelectionChange, isCheckboxesDisabled } =
    rowSelectionConfig || {};

  useEffect(() => {
    if (rowSelectionConfig?.selectedRowsState) {
      setRowSelection(rowSelectionConfig.selectedRowsState);
    }
  }, [rowSelectionConfig?.selectedRowsState]);

  const memoizedColumns = useMemo(() => {
    if (!enableRowSelection) return columns;

    return [
      geTableSelectionColumn(isCheckboxesDisabled),
      ...columns
    ] as ColumnDef<T>[];
  }, [columns, enableRowSelection, isCheckboxesDisabled]);

  const memoizedData = useMemo(() => data, [data]);
  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: controlledPageIndex ?? 0,
    pageSize: rowsPerPage
  });

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue
  ) => {
    const newState: PaginationState =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(localPagination)
        : updaterOrValue;

    if (manualPagination) {
      onPageChange?.(newState.pageIndex);
    } else {
      setLocalPagination(newState);
    }
  };

  const table = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    getRowId,
    pageCount: manualPagination ? controlledPageCount : undefined,
    state: {
      rowSelection,
      pagination: manualPagination
        ? { pageIndex: controlledPageIndex!, pageSize: rowsPerPage }
        : localPagination
    },
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      if (onRowSelectionChange) {
        const selectedRows = table
          .getPrePaginationRowModel()
          .rows.filter((row) => newSelection[row.id])
          .map((row) => row.original);
        onRowSelectionChange(selectedRows);
      }
    }
  });

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-[200px]">
          <CircularProgress className="h-12" />
        </div>
      ) : emptyStateConfig?.isEmpty ? (
        <EmptyState
          title={`Add your first ${emptyStateConfig.title}`}
          description={emptyStateConfig.description}
          buttonText={`Create New ${emptyStateConfig.title}`}
          onClick={emptyStateConfig.onClick}
        />
      ) : (
        <div
          className={cn(
            'border-base-sidebarBorder rounded-[20px] overflow-hidden',
            bordered ? 'border' : 'border-0'
          )}
        >
          <Table
            className={cn(
              'w-full table-fixed [&_td:last-child]:w-[60px] [&_td:last-child]:max-w-[60px] [&_th:last-child]:w-[60px] [&_th:last-child]:max-w-[60px]',
              className
            )}
            style={{ tableLayout: 'fixed' }}
          >
            {showHeader && (
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map(
                      (header: Header<T, any>, index: number) => (
                        <TableHead
                          key={header.id}
                          id={header.id}
                          className={cn(
                            'py-4 px-4 text-base-mutedForeground text-p-s font-normal text-left align-top',
                            'last:text-right',
                            header.id === SELECT_ALL_ROWS_ID && 'w-12'
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      )
                    )}
                  </TableRow>
                ))}
              </TableHeader>
            )}
            <TableBody data-testid={bodyDataTestId}>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row: Row<T>) => (
                  <TableRow
                    data-testid={
                      rowIdIdentifier
                        ? (row.original as Record<string, string>)[
                            rowIdIdentifier
                          ]
                        : undefined
                    }
                    key={row.id}
                    className={cn(
                      'first:border-t-0 box-border',
                      'hover:bg-base-muted',
                      sizeClasses[size as Size]
                    )}
                  >
                    {row.getVisibleCells().map((cell: Cell<T, any>) => (
                      <TableCell
                        data-testid={cell.column.id}
                        key={cell.id}
                        className={cn(
                          'px-4 border-t text-left text-base-foreground text-p-s min-w-0 max-w-0',
                          'last:text-right'
                        )}
                        style={{ overflow: 'hidden' }}
                      >
                        <Tooltip
                          title={String(cell.getValue() || '')}
                          arrow
                          placement="top"
                        >
                          <div
                            className={cn(
                              'inline-flex items-center justify-start w-full',
                              'whitespace-nowrap overflow-hidden text-ellipsis max-w-full',
                              '[&::-webkit-scrollbar]:hidden'
                            )}
                            style={{
                              minWidth: 0,
                              color: 'var(--base-sidebar-foreground)'
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center py-10"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {showPagination && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="px-5 text-base-foreground text-p-s"
                  >
                    <DataTablePagination table={table} rowCount={rowCount} />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      )}
    </>
  );
};

export default DataTable;
